/**
 * 확장 기여 레지스트리 (ADR 0024)
 * --------------------------------------------------------------------------
 * 번들에 들어간 확장 매니페스트(생성 산출물)와 지연 로더를 묶는 **타입이 붙은 표**입니다.
 * 매니페스트는 빌드 시점에 이미 검증되었으므로 여기서 다시 검증하지 않고, 대신
 * "선언과 로더가 짝이 맞는지"만 테스트가 확인합니다(`registryIntegrityErrors`).
 *
 * 경계
 * - 확장은 자기 데이터소스를 갖지 않습니다. 화면 코드가 쓰는 통로는 기존과 같은
 *   `@/api/client`(BFF)뿐이고, 이 파일은 네트워크를 하지 않습니다.
 * - 능력 판정은 **호환성**만 봅니다. 권한은 서버가 요청마다 강제합니다.
 */
import type { ComponentType } from "react";
import type { ExtensionManifest, ExtensionRoute } from "@k8s-dashboard/extensions";
import type { ExtensionCapabilitySnapshot } from "@/api/capabilities";
import { EXTENSION_API_VERSION } from "@/generated/extensions";
import { EXTENSION_MANIFESTS } from "./contributions";
import { EXTENSION_LOADERS, type ExtensionModuleExports } from "./loaders";

export const EXTENSIONS: readonly ExtensionManifest[] = EXTENSION_MANIFESTS;
export const HOST_EXTENSION_API_VERSION = EXTENSION_API_VERSION;

/** 화면이 꺼지는 이유. 사용자에게 "빈 화면"이 아니라 이유를 보여주기 위한 닫힌 집합입니다. */
export type ExtensionDisabledReason =
  | "unknown_extension"
  | "capabilities_unavailable"
  | "api_major_mismatch"
  | "backend_missing"
  | "backend_major_mismatch"
  | "backend_minor_behind"
  | "capability_missing";

export const EXTENSION_DISABLED_LABEL: Record<ExtensionDisabledReason, string> = {
  unknown_extension: "이 빌드에 등록되지 않은 확장입니다.",
  capabilities_unavailable: "서버 기능 목록을 확인하지 못했습니다.",
  api_major_mismatch: "서버와 화면의 확장 API major 버전이 다릅니다.",
  backend_missing: "서버 빌드에 이 기능이 등록되어 있지 않습니다.",
  backend_major_mismatch: "서버와 화면의 확장 계약 major 버전이 다릅니다.",
  backend_minor_behind: "서버 확장 계약 버전이 이 화면보다 낮습니다.",
  capability_missing: "이 기능이 쓰는 서버 엔드포인트가 등록되어 있지 않습니다.",
};

/**
 * 확장 하나의 가용성. **세 상태**입니다 — 확인 중(pending)은 "켜짐"이 아닙니다.
 * 확인되기 전에는 목적지·명령을 노출하지 않고, 직접 들어온 라우트만 유계 로딩을 보여줍니다.
 */
export type ExtensionAvailability =
  | { status: "pending" }
  | { status: "enabled" }
  | { status: "disabled"; reason: ExtensionDisabledReason };

export interface ExtensionRouteEntry {
  manifest: ExtensionManifest;
  route: ExtensionRoute;
}

export function extensionById(id: string): ExtensionManifest | undefined {
  return EXTENSIONS.find((manifest) => manifest.id === id);
}

/** 확장 id + 라우트 id로 기여를 찾습니다. 없으면 호출부의 오타입니다. */
export function extensionRouteEntry(extensionId: string, routeId: string): ExtensionRouteEntry | undefined {
  const manifest = extensionById(extensionId);
  const route = manifest?.routes.find((r) => r.id === routeId);
  return manifest && route ? { manifest, route } : undefined;
}

function parseVersion(value: string): [number, number, number] | undefined {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(value);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : undefined;
}

/**
 * 서버가 등록했다고 답한 능력과 매니페스트를 대조합니다.
 *
 * **fail closed입니다.** 능력을 아직 모르면 pending, 확인에 실패하면 disabled입니다 —
 * 어느 쪽도 "켜짐"이 아닙니다. 서버가 모르는 기능을 화면이 먼저 광고하지 않습니다.
 * (접근 통제 자체는 여전히 서버가 요청마다 강제합니다. 이 판정은 그 위의 호환성 층입니다.)
 */
export function extensionAvailability(
  manifest: ExtensionManifest | undefined,
  snapshot: ExtensionCapabilitySnapshot,
): ExtensionAvailability {
  if (!manifest) return { status: "disabled", reason: "unknown_extension" };
  if (snapshot.status === "pending") return { status: "pending" };
  if (snapshot.status === "error") return { status: "disabled", reason: "capabilities_unavailable" };
  const capabilities = snapshot.response;
  /* 확장 API 자체의 major가 다르면 개별 확장을 볼 필요가 없습니다 — 계약 전체가 다릅니다. */
  if (capabilities.apiVersion.split(".")[0] !== HOST_EXTENSION_API_VERSION.split(".")[0]) {
    return { status: "disabled", reason: "api_major_mismatch" };
  }
  const served = capabilities.extensions.find((entry) => entry.id === manifest.id);
  if (!served) return { status: "disabled", reason: "backend_missing" };
  const local = parseVersion(manifest.version);
  const remote = parseVersion(served.version);
  if (!local || !remote || local[0] !== remote[0]) return { status: "disabled", reason: "backend_major_mismatch" };
  if (remote[1] < local[1]) return { status: "disabled", reason: "backend_minor_behind" };
  const endpoints = new Set(served.endpoints);
  const queryRefs = new Set(served.queryRefs);
  if (manifest.backend.endpoints.some((endpoint) => !endpoints.has(endpoint))) return { status: "disabled", reason: "capability_missing" };
  if (manifest.backend.queryRefs.some((ref) => !queryRefs.has(ref))) return { status: "disabled", reason: "capability_missing" };
  return { status: "enabled" };
}

/**
 * 지금 켜도 되는 확장 id 집합. nav·팔레트가 이 집합으로 기여를 거릅니다.
 * 능력을 모르거나 확인에 실패하면 **빈 집합**입니다.
 */
export function enabledExtensionIds(snapshot: ExtensionCapabilitySnapshot): ReadonlySet<string> {
  if (snapshot.status !== "ready") return new Set();
  return new Set(EXTENSIONS.filter((manifest) => extensionAvailability(manifest, snapshot).status === "enabled").map((m) => m.id));
}

/* ── 지연 모듈 ────────────────────────────────────────────────────────────── */

const inFlight = new Map<string, Promise<ExtensionModuleExports>>();

/**
 * 확장 모듈을 한 번만 가져옵니다. 실패한 약속은 캐시에서 지웁니다 — 남겨두면
 * "다시 시도"가 같은 실패를 즉시 되돌려줍니다.
 */
export function loadExtensionModule(manifest: ExtensionManifest): Promise<ExtensionModuleExports> {
  const specifier = manifest.module.specifier;
  const cached = inFlight.get(specifier);
  if (cached) return cached;
  const loader = EXTENSION_LOADERS[specifier];
  if (!loader) return Promise.reject(new Error("extension loader is not registered"));
  const promise = loader().catch((error: unknown) => {
    inFlight.delete(specifier);
    throw error;
  });
  inFlight.set(specifier, promise);
  return promise;
}

/**
 * 확장 하나의 캐시 항목만 지웁니다. 시간 초과 뒤 "다시 시도"가 실제로 다시 부르려면
 * 아직 끝나지 않은 약속을 버려야 합니다 — 남겨두면 같은 약속을 또 기다립니다.
 * 버려진 약속이 나중에 끝나도 그때의 effect는 이미 정리되어 상태를 바꾸지 못합니다.
 */
export function evictExtensionModule(manifest: ExtensionManifest) {
  inFlight.delete(manifest.module.specifier);
}

/** 테스트가 모듈 캐시를 비웁니다. 프로덕션 경로에서는 호출하지 않습니다. */
export function resetExtensionModuleCache() {
  inFlight.clear();
}

/** 선언된 export가 실제로 컴포넌트인지 확인합니다. 아니면 로드 실패로 다룹니다. */
export function resolveExtensionComponent(exports: ExtensionModuleExports, name: string): ComponentType | undefined {
  const candidate = exports[name];
  return typeof candidate === "function" ? (candidate as ComponentType) : undefined;
}

/**
 * 선언과 로더가 짝이 맞는지 확인합니다. 빌드 검사가 이미 같은 사실을 보지만,
 * 여기서도 값으로 노출해 단위 테스트가 회귀를 잡습니다.
 */
export function registryIntegrityErrors(): string[] {
  const errors: string[] = [];
  const specifiers = new Set(Object.keys(EXTENSION_LOADERS));
  for (const manifest of EXTENSIONS) {
    if (!specifiers.delete(manifest.module.specifier)) errors.push(`${manifest.id}: loader is missing for ${manifest.module.specifier}`);
    const [major] = manifest.apiVersion.split(".");
    if (major !== HOST_EXTENSION_API_VERSION.split(".")[0]) errors.push(`${manifest.id}: apiVersion major differs from host`);
  }
  for (const orphan of specifiers) errors.push(`loader is not declared by any manifest: ${orphan}`);
  return errors;
}
